import { useMemo } from 'react';
import { Link as LinkIcon } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useHiddenCategories } from './gameCategories';
import {
  BOTTOM_NAV_ICONS, ROUTE_CATEGORIES, knownIcon, pathOf,
} from './bottomNav';

/**
 * Üst menünün kısayolları — footer menüsüne bağlıdır.
 *
 * Kategoriler (Anasayfa, Spor, Slotlar …) kabuğun kendi listesinden gelir;
 * onların ardına eklenen kısayollar sabit kodlu değildir: yönetim panelindeki
 * **Footer Yönetimi → menü grupları** kayıtlarından, "Üst menüde göster"
 * işaretli grupların bağlantılarından türetilir. Böylece aynı bağlantı iki
 * ayrı yerden yönetilmez.
 *
 * Ayar kaydı `footer_menu_groups` içindedir; hiçbir grup işaretli değilse üst
 * menü yalnızca kategorilerden oluşur.
 */

// İkon anahtarları alt menüyle ortaktır (BOTTOM_NAV_ICONS); panelde aynı liste
// gösterilir. Seçilmemiş ya da tanınmayan değer bağlantı ikonuna düşer.
export const topNavIcon = (name) => (knownIcon(name) ? BOTTOM_NAV_ICONS[name] : LinkIcon);

const isExternal = (url) => /^(https?:)?\/\//i.test(String(url || ''));

/** İşaretli gruplardaki bağlantıları çizilebilir tek listeye indirger.
 *
 * ``flag`` hangi işaretin okunacağını söyler: ``in_topnav`` üst menü,
 * ``in_rail_foot`` Betrun sol menüsünün altındaki iletişim kutusu.
 */
export const collectTopNavLinks = (groups, flag = 'in_topnav') => {
  const items = [];
  const seen = new Set();
  for (const group of Array.isArray(groups) ? groups : []) {
    if (!group?.[flag]) continue;
    for (const link of Array.isArray(group.links) ? group.links : []) {
      const label = String(link?.label || '').trim();
      const url = String(link?.url || '').trim();
      // Aynı adres iki grupta da işaretliyse üst menüde bir kez görünür.
      if (!label || !url || seen.has(url)) continue;
      seen.add(url);
      items.push({
        to: url,
        label,
        icon: topNavIcon(link?.icon),
        external: isExternal(url),
        newTab: Boolean(link?.new_tab),
      });
    }
  }
  return items;
};

/**
 * Üst menüde gösterilecek kısayollar.
 *
 * Kapalı bir bölüme giden bağlantı listeden düşer: spor kapalıyken `/spor`,
 * kategori kapalıyken o kategorinin sayfası, çark kapalıyken `/cark`, çapa
 * etkinliği kapalıyken `/capa`, yayında çekiliş yokken `/cekilis` — yoksa
 * oyuncu boş ya da "şu anda kapalı" sayfasına gider.
 */
export const useTopNavLinks = () => {
  const settings = useSettings();
  const hiddenCats = useHiddenCategories();
  return useMemo(() => collectTopNavLinks(settings.footer_menu_groups).filter((item) => {
    if (item.external) return true;
    const path = pathOf(item.to);
    if (path === '/spor') {
      return settings.settingsLoaded && settings.sports_enabled !== false;
    }
    if (path === '/cark') return settings.wheel_enabled === true;
    if (path === '/capa') return settings.anchor_enabled === true;
    if (path === '/cekilis') return settings.raffle_active === true;
    if (path === '/etkinlik') return settings.prediction_active === true;
    const category = ROUTE_CATEGORIES[path];
    return !category || !hiddenCats.includes(category);
  }), [
    settings.footer_menu_groups, settings.settingsLoaded,
    settings.sports_enabled, settings.wheel_enabled, settings.anchor_enabled,
    settings.raffle_active, settings.prediction_active,
    hiddenCats,
  ]);
};

/**
 * Betrun sol menüsünün altındaki iletişim kutusuna eklenen bağlantılar
 * (BetRunTV gibi). Panelde: Footer Yönetimi → menü grubu → "Sol menü altında
 * göster". Kutunun sabit maddeleri (Beni Ara, Telegram, Canlı Destek) kabukta
 * durur; buraya yalnızca yöneticinin eklediği adresler gelir.
 */
export const useRailFootLinks = () => {
  const settings = useSettings();
  return useMemo(
    () => collectTopNavLinks(settings.footer_menu_groups, 'in_rail_foot'),
    [settings.footer_menu_groups],
  );
};

export default useTopNavLinks;
